import { useEffect, useState } from 'react';

/** The lazily loaded help-content module (articles + lookup helpers). */
export type HelpArticlesModule = typeof import('./articles');

/** Module-level cache so every consumer shares one chunk load. */
let loaded: HelpArticlesModule | null = null;
let pending: Promise<HelpArticlesModule> | null = null;

/**
 * Load the help-article catalogue from its own chunk.
 *
 * WHY lazy: the article copy is the largest static payload in the app and it is
 * only needed once someone opens the help drawer or types in global search —
 * never for first paint. Keeping it out of the entry chunk holds the initial
 * payload inside its budget (docs/engineering/frontend-performance.md) as the
 * guide keeps growing. The promise is memoised so concurrent callers share it.
 */
export function loadHelpArticles(): Promise<HelpArticlesModule> {
  if (loaded !== null) {
    return Promise.resolve(loaded);
  }
  pending ??= import('./articles').then((module) => {
    loaded = module;
    return module;
  });
  return pending;
}

/**
 * The help-article module once loaded, else `null` (callers render nothing
 * help-related until then — the load is a few milliseconds after mount).
 */
export function useHelpArticles(): HelpArticlesModule | null {
  const [module, setModule] = useState<HelpArticlesModule | null>(loaded);

  useEffect(() => {
    if (module !== null) {
      return undefined;
    }
    let active = true;
    loadHelpArticles()
      .then((next) => {
        if (active) {
          setModule(next);
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [module]);

  return module;
}
